import { buildTranslate } from 'locales'
import React, { useEffect } from 'react'
import { Prompt } from 'react-router-dom'

const t = buildTranslate('recovery_ai.settings')

export type UseUnsavedChangesPromptOptions = {
  /**
   * AI-900: suppress ONLY the in-app <Prompt> (route navigation) while true —
   * the beforeunload/tab-close guard stays armed regardless. For a pane whose
   * edits debounce-autosave, an in-app nav during the pending window is safe
   * (the unmount this triggers now FLUSHES the armed/queued commit — see
   * useActiveLocationsController's cleanup), but a tab close/refresh cannot be
   * flushed reliably, so that channel must keep warning on the same `isDirty`
   * it always has. Defaults to false for every other pane.
   */
  skipInAppPrompt?: boolean
}

// AI-121 (R3): shared unsaved-changes guard for the RecoveryAI Settings panes.
// When `isDirty` is true it warns the merchant before they navigate away with
// pending edits, via two complementary channels:
//
//   1. react-router-dom v4 <Prompt> — catches IN-APP route navigation (clicking
//      a nav link, a programmatic push). The returned element must be rendered
//      by the consuming pane for this to take effect.
//   2. a `beforeunload` listener — catches a browser refresh / tab close, which
//      <Prompt> can't see. Wired/torn-down here so the panes don't duplicate it.
//
// AI-174 (Bug 1): `message` is a FUNCTION so Save-and-test's own sanctioned
// navigation can proceed WITHOUT the confirm. That navigation fires the moment
// the PUT resolves, but `isDirty` (which gates <Prompt>) reads the RTK Query
// cache, which only refreshes after the post-save refetch — so `isDirty` can
// still be true at push time, producing a bogus warning. useSaveAndTest flips
// `guardBypassRef` right before the push; here the message returns `true`
// (allow the transition, no confirm) while that flag is set. A normal user
// navigation (flag false) still gets the translated warning.
export function useUnsavedChangesPrompt(
  isDirty: boolean,
  guardBypassRef?: React.MutableRefObject<boolean>,
  options?: UseUnsavedChangesPromptOptions
): React.ReactElement {
  const skipInAppPrompt = options?.skipInAppPrompt ?? false

  useEffect(() => {
    if (!isDirty) return
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      // Spec-compliant way to trigger the browser's native "leave site?" dialog:
      // preventDefault + assign returnValue. The browser shows its own generic
      // copy here (custom strings were removed from the spec), so there's no
      // i18n string to thread through this path — the <Prompt> below carries the
      // translated message for in-app navigation.
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [isDirty])

  return (
    <Prompt
      when={isDirty && !skipInAppPrompt}
      message={() =>
        guardBypassRef?.current ? true : t('unsaved_changes_prompt')
      }
    />
  )
}
